import Avatar from '@/components/Avatar';
import Constants from '@/Constants';
import { DialogFlowMessage } from '@/models/Chat';
import ChipsMessage from '@/pages/Chat/ChatChipsMessage';
import ChatUserMessage from '@/pages/Chat/ChatUserMessage';
import ImageMessage from '@/pages/Chat/ImageMessage';
import TextMessage from '@/pages/Chat/TextMessage';
import { AnsweredMessage } from '@/store/bot/types';
import React from 'react';
import { last } from 'underscore';
import ChatActionMessage from '../ChatActionMessage';
import LoadingMessage from './LoadingMessage';
import { ContentProps, MessageProps, MessageType, PayloadType } from './types';

const ActionMessage: React.FC<MessageProps> = ({ message }: MessageProps) => <ChatActionMessage message={message} />;

const UserMessage: React.FC<MessageProps> = ({ message }: MessageProps) => <ChatUserMessage value={message.message} />;

const BotMessage: React.FC<MessageProps> = ({ message }: MessageProps) => (
    <div className="bot-message message message-left">
        <TextMessage isReplyMessage={false} message={message.message} />
    </div>
);

const AnsweredComponent: React.FC<MessageProps> = ({ message }: MessageProps) => {
    const content = message.payload.richContent[0][0];
    const options = content.options || [];

    const answers: number[] = (message as AnsweredMessage).answer.options;

    return (
        <>
            {answers.map((optionIndex: number, index: number) => (
                <ChatUserMessage key={`answered_chip_${index}`} value={options[optionIndex].text} />
            ))}
        </>
    );
};

const PayloadMessage: React.FC<MessageProps> = (props: MessageProps) => {
    const { message, lastMessage } = props;
    const content = message.payload.richContent[0][0];
    const answered = Boolean((message as AnsweredMessage).answer);
    const isRecommendation: boolean = content.path === Constants.chatBotActionPaths.RECOMENDATION;
    const isImage: boolean = content.type === PayloadType.IMAGE;

    if (answered) {
        return <AnsweredComponent {...props} />;
    }

    if (!answered && message !== lastMessage && !isRecommendation && !isImage) {
        return null;
    }

    const Component = chatPayloadMessageByType[content.type] as React.FC<MessageProps>;

    return (
        <div className="message message-left">
            <Component {...props} />
        </div>
    );
};

const chatPayloadMessageByType: Record<PayloadType, React.FC<MessageProps>> = {
    [PayloadType.CHIPS]: ChipsMessage,
    [PayloadType.IMAGE]: ImageMessage,
    [PayloadType.ACTION]: ActionMessage,
};

const chatMessageByMessageType: Record<MessageType, React.FC<MessageProps>> = {
    [MessageType.USER]: UserMessage,
    [MessageType.PAYLOAD]: PayloadMessage,
    [MessageType.MESSAGE]: BotMessage,
};

const ChatContent: React.FC<ContentProps> = (props: ContentProps) => {
    const {
        chat: { messages, loading },
        sendMessage,
        buffering,
        positionY,
    } = props;

    let count = 0;

    const [remaining, progress] = buffering;

    const lastMessage = last(messages);

    const rendered = messages.reduce((acc: React.ReactElement[], message: DialogFlowMessage, index: number) => {
        // renderiza as mensagens do bot até enquanto
        // o `count == progress`.
        const botMessage = message.messageType !== 'USER';
        if (count === progress && botMessage) {
            return acc;
        }
        if (botMessage) {
            count++;
        }

        const Component = chatMessageByMessageType[message.messageType];
        acc.push(<Component key={`message-${index}`} {...{ lastMessage, message, sendMessage, index }} />);

        return acc;
    }, []);

    const hasPendingMessages = remaining - progress > 0;

    return (
        <div className="chat-messages" key="chat-messages">
            <Avatar positionY={positionY} size={48} />
            {rendered}
            {(loading || hasPendingMessages) && <LoadingMessage />}
        </div>
    );
};

export default ChatContent;
